import { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, CheckCircle2, Clock, AlertTriangle, Trash2, Edit } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_COLORS = {
    LOW: 'bg-gray-100 text-gray-600',
    MEDIUM: 'bg-blue-100 text-blue-600',
    HIGH: 'bg-orange-100 text-orange-600',
    URGENT: 'bg-red-100 text-red-600'
};

const PRIORITY_LABELS = {
    LOW: 'Baja',
    MEDIUM: 'Media',
    HIGH: 'Alta',
    URGENT: 'Urgente'
};

const STATUS_COLORS = {
    PENDING: 'bg-gray-100 text-gray-700',
    IN_PROGRESS: 'bg-blue-100 text-blue-700',
    COMPLETED: 'bg-green-100 text-green-700',
    CANCELLED: 'bg-gray-100 text-gray-500'
};

const STATUS_LABELS = {
    PENDING: 'Pendiente',
    IN_PROGRESS: 'En Progreso',
    COMPLETED: 'Completada',
    CANCELLED: 'Cancelada'
};

export const TasksList = ({ procedureId, token, onRefresh }) => {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (procedureId) {
            fetchTasks();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [procedureId]);

    async function fetchTasks() {
        try {
            const res = await axios.get(`${API}/procedures/${procedureId}/tasks`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTasks(res.data);
        } catch (error) {
            console.error('Error fetching tasks:', error);
            toast.error('Error al cargar tareas');
        } finally {
            setLoading(false);
        }
    }

    const handleCompleteTask = async (taskId) => {
        try {
            await axios.post(`${API}/tasks/${taskId}/complete`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Tarea completada');
            fetchTasks();
            if (onRefresh) onRefresh();
        } catch (error) {
            console.error('Error completing task:', error);
            toast.error('Error al completar tarea');
        }
    };

    const handleDeleteTask = async (taskId) => {
        if (!window.confirm('¿Estás seguro de eliminar esta tarea?')) return;

        try {
            await axios.delete(`${API}/tasks/${taskId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Tarea eliminada');
            fetchTasks();
            if (onRefresh) onRefresh();
        } catch (error) {
            console.error('Error deleting task:', error);
            toast.error('Error al eliminar tarea');
        }
    };

    const isOverdue = (dueDate, status) => {
        if (status === 'COMPLETED' || status === 'CANCELLED') return false;
        return new Date(dueDate) < new Date();
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        if (date.toDateString() === today.toDateString()) {
            return 'Hoy';
        } else if (date.toDateString() === tomorrow.toDateString()) {
            return 'Mañana';
        }

        return date.toLocaleDateString('es-MX', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
        );
    }

    if (tasks.length === 0) {
        return (
            <div className="text-center py-12 text-gray-500">
                <CheckCircle2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p>No hay tareas registradas</p>
                <p className="text-sm mt-2">Crea una nueva tarea para comenzar</p>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {tasks.map(task => {
                const overdue = isOverdue(task.due_date, task.status);

                return (
                    <div
                        key={task.id}
                        className={`p-4 border rounded-lg transition-colors ${overdue
                                ? 'border-red-300 bg-red-50'
                                : task.status === 'COMPLETED'
                                    ? 'border-green-200 bg-green-50'
                                    : 'border-gray-200 bg-white hover:bg-gray-50'
                            }`}
                    >
                        <div className="flex items-start gap-3">
                            {/* Checkbox for completion */}
                            <div className="pt-1">
                                <Checkbox
                                    checked={task.status === 'COMPLETED'}
                                    onCheckedChange={() => {
                                        if (task.status !== 'COMPLETED') {
                                            handleCompleteTask(task.id);
                                        }
                                    }}
                                    disabled={task.status === 'COMPLETED'}
                                />
                            </div>

                            {/* Task content */}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1">
                                        <h4 className={`text-sm font-medium ${task.status === 'COMPLETED' ? 'text-gray-500 line-through' : 'text-gray-900'
                                            }`}>
                                            {task.title}
                                        </h4>
                                        {task.description && (
                                            <p className="text-xs text-gray-500 mt-1">{task.description}</p>
                                        )}
                                    </div>

                                    {/* Actions menu */}
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                                •••
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="bg-white border-gray-200">
                                            <DropdownMenuLabel>Acciones</DropdownMenuLabel>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem
                                                onClick={() => toast.info('Editar tarea en desarrollo')}
                                                className="cursor-pointer"
                                            >
                                                <Edit className="h-4 w-4 mr-2" />
                                                Editar
                                            </DropdownMenuItem>
                                            <DropdownMenuItem
                                                onClick={() => handleDeleteTask(task.id)}
                                                className="cursor-pointer text-red-600"
                                            >
                                                <Trash2 className="h-4 w-4 mr-2" />
                                                Eliminar
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>

                                {/* Task metadata */}
                                <div className="flex items-center gap-3 mt-2 flex-wrap">
                                    {/* Priority badge */}
                                    <Badge className={`${PRIORITY_COLORS[task.priority]} border-0 text-xs`}>
                                        {PRIORITY_LABELS[task.priority]}
                                    </Badge>

                                    {/* Status badge */}
                                    <Badge className={`${STATUS_COLORS[task.status]} border-0 text-xs`}>
                                        {task.status === 'COMPLETED' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                                        {STATUS_LABELS[task.status]}
                                    </Badge>

                                    {/* Due date */}
                                    <div className={`flex items-center gap-1 text-xs ${overdue ? 'text-red-600 font-medium' : 'text-gray-500'
                                        }`}>
                                        {overdue ? (
                                            <AlertTriangle className="h-3 w-3" />
                                        ) : (
                                            <Clock className="h-3 w-3" />
                                        )}
                                        <span>
                                            {overdue ? 'Vencida: ' : 'Vence: '}
                                            {formatDate(task.due_date)}
                                        </span>
                                    </div>

                                    {/* Assigned user */}
                                    {task.assigned_to_user_id && (
                                        <div className="text-xs text-gray-500">
                                            Asignado a: {task.assigned_to_user_id.slice(0, 8)}...
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
